import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useEffectiveRole, useCanSwitchView, VIEW_MODE_OPTIONS } from "@/lib/effectiveRole";
import {
  Bell, SignOut, UserCircle, Share, Eye,
  Calendar, FolderOpen, Question,
} from "@phosphor-icons/react";

const ROLE_LABEL = {
  investor: "Inversor",
  constructor: "Constructor",
  marketing: "Marketing",
  admin: "Admin",
};

/** Investor dropdown entry — dispatch `dv:investor-tab` to InvestorDashboard. */
function InvestorTabItem({ label, icon: Icon, tab, setOpen }) {
  return (
    <li>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          setOpen(false);
          window.dispatchEvent(new CustomEvent("dv:investor-tab", { detail: tab }));
        }}
        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-stone-800 dark:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800 active:bg-stone-100 dark:active:bg-stone-700 outline-none focus-visible:bg-stone-50 dark:focus-visible:bg-stone-800"
      >
        <Icon size={18} />
        {label}
      </button>
    </li>
  );
}

/**
 * Mobile-only top app bar. Visible below `lg`. The avatar opens a small
 * account dropdown holding user summary, an optional "Mi cuenta" action
 * (role-aware: investor switches tab via window event; admin navigates to
 * /settings; other roles see summary + logout only) and "Cerrar sesión".
 *
 * `topInset` — ¿es este header el elemento más alto de la pantalla? El hueco
 * de la barra de estado de iOS lo aplica UNO solo: el de arriba del todo. Con
 * el aviso de vista previa encima (Shell, z-50) ese no es este header, y
 * repetir el inset dejaba un hueco muerto entre los dos.
 */
export default function MobileAppHeader({ title, accent = "#1C1C1E", showBell = false, topInset = true }) {
  const { user, logout, viewMode, setViewMode } = useAuth();
  const effectiveRole = useEffectiveRole();
  const canSwitchView = useCanSwitchView();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("touchstart", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;
  // Navigation chrome follows the (admin-only) view override; the real role
  // still drives anything permission-sensitive.
  const role = effectiveRole || user.role || "investor";
  const initial = (user.name || user.email || "U")[0]?.toUpperCase() || "U";
  const roleLabel = ROLE_LABEL[role] || role;

  // Role-aware "Mi cuenta" target. Returns null when no valid destination.
  const onAccountClick = () => {
    setOpen(false);
    if (role === "investor") {
      window.dispatchEvent(new CustomEvent("dv:investor-tab", { detail: "my_account" }));
      return;
    }
    if (role === "admin") {
      navigate("/settings");
      return;
    }
  };
  const accountAvailable = role === "investor" || role === "admin";

  return (
    <header
      ref={rootRef}
      className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-2 border-b border-stone-200 dark:border-stone-800 bg-white/95 dark:bg-stone-900/95 backdrop-blur"
      style={{ paddingTop: topInset ? "max(env(safe-area-inset-top), 8px)" : "8px" }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center text-white font-serif-dv text-sm shrink-0"
          style={{ background: accent }}
        >
          D
        </div>
        <div className="min-w-0">
          <div className="font-serif-dv text-base leading-tight truncate text-stone-900 dark:text-stone-50">
            {title || "Dolce Villas"}
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 dark:text-stone-400 truncate">
            {roleLabel}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0 relative">
        {showBell && role !== "investor" && (
          <span className="w-8 h-8 flex items-center justify-center rounded-full text-stone-600 dark:text-stone-300" aria-hidden="true">
            <Bell size={18} />
          </span>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Abrir menú de cuenta"
          className="outline-none focus-visible:ring-2 focus-visible:ring-stone-300 dark:focus-visible:ring-stone-600 rounded-full"
        >
          {user.picture ? (
            <img
              src={user.picture}
              alt=""
              className="w-8 h-8 rounded-full object-cover border border-stone-200 dark:border-stone-700"
            />
          ) : (
            <span
              className="w-8 h-8 flex items-center justify-center rounded-full text-white text-xs"
              style={{ background: accent }}
            >
              {initial}
            </span>
          )}
        </button>

        {open && (
          <div
            role="menu"
            className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 shadow-[0_8px_24px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.5)] overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-stone-100 dark:border-stone-800">
              <div className="text-sm font-medium text-stone-900 dark:text-stone-50 truncate">
                {user.name || "Usuario"}
              </div>
              {user.email && (
                <div className="text-xs text-stone-500 dark:text-stone-400 truncate">{user.email}</div>
              )}
              <div className="mt-1 inline-block text-[10px] tracking-[0.2em] uppercase text-stone-500 dark:text-stone-400">
                {roleLabel}
              </div>
            </div>
            <ul className="py-1">
              {role === "investor" && (
                <>
                  <InvestorTabItem
                    label="Pagos"
                    icon={Calendar}
                    tab="payments"
                    setOpen={setOpen}
                  />
                  <InvestorTabItem
                    label="Documentos"
                    icon={FolderOpen}
                    tab="documents"
                    setOpen={setOpen}
                  />
                  <InvestorTabItem
                    label="Referidos"
                    icon={Share}
                    tab="referral"
                    setOpen={setOpen}
                  />
                </>
              )}
              {accountAvailable && (
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={onAccountClick}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-stone-800 dark:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800 active:bg-stone-100 dark:active:bg-stone-700 outline-none focus-visible:bg-stone-50 dark:focus-visible:bg-stone-800"
                  >
                    <UserCircle size={18} />
                    Mi cuenta
                  </button>
                </li>
              )}
              {role === "investor" && (
                <InvestorTabItem
                  label="FAQ / Ayuda"
                  icon={Question}
                  tab="help"
                  setOpen={setOpen}
                />
              )}
              {canSwitchView && (
                <li className="border-t border-stone-100 dark:border-stone-800 px-4 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 mb-1 flex items-center gap-1">
                    <Eye size={11} /> Ver como
                  </div>
                  <select
                    value={viewMode || "admin"}
                    onChange={(e) => { setViewMode(e.target.value); setOpen(false); }}
                    className="w-full text-sm border border-stone-200 dark:border-stone-700 rounded px-2 py-1 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100"
                    data-testid="mobile-view-switcher"
                  >
                    {VIEW_MODE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </li>
              )}
              <li>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    logout?.();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-stone-800 dark:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800 active:bg-stone-100 dark:active:bg-stone-700 outline-none focus-visible:bg-stone-50 dark:focus-visible:bg-stone-800"
                >
                  <SignOut size={18} />
                  Cerrar sesión
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
}
